/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `useDeviceDetection` for the Caintra Connect frontend.
 * @module hooks/useDeviceDetection
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useState, useEffect } from "react";

const useDeviceDetection = () => {
  const [deviceType, setDeviceType] = useState("desktop");

  useEffect(() => {
    const checkDeviceType = () => {
      const width = window.innerWidth;

      if (width <= 480) {
        setDeviceType("smallMobile");
      } else if (width <= 767) {
        setDeviceType("mediumMobile");
      } else if (width <= 1023) {
        setDeviceType("tablet");
      } else if (width <= 1440) {
        setDeviceType("laptop");
      } else {
        setDeviceType("desktop");
      }
    };

    // Check on mount
    checkDeviceType();

    // Add event listener for window resize
    window.addEventListener("resize", checkDeviceType);

    // Cleanup
    return () => window.removeEventListener("resize", checkDeviceType);
  }, []);

  return {
    deviceType,
    isTablet: deviceType === "tablet",
    isSmallMobile: deviceType === "smallMobile",
    isMediumMobile: deviceType === "mediumMobile",
    isMobile: deviceType === "smallMobile" || deviceType === "mediumMobile",
    isLaptop: deviceType === "laptop",
    isDesktop: deviceType === "desktop",
  };
};

export default useDeviceDetection;
